/**
 * Hook para tracking de Goals do DataFast
 * Centraliza todos os eventos de conversão da jornada do usuário
 */

interface GoalParams {
  [key: string]: string | number;
}

interface IdentifyParams {
  user_id: string;
  name?: string;
  image?: string;
  [key: string]: string | number | boolean | undefined;
}

declare global {
  interface Window {
    datafast?: {
      (goalName: string, params?: GoalParams): void;
      (command: "identify", params: IdentifyParams): void;
      q?: any[];
    };
    gtag?: (...args: any[]) => void;
  }
}

import { useMemo } from "react";

export const useDataFastGoals = () => {
  return useMemo(() => {
    /**
     * Função auxiliar para enviar goals ao DataFast
     * @param goalName - Nome do goal (lowercase, max 64 chars)
     * @param params - Parâmetros customizados (max 10 params, max 255 chars cada)
     */
    const trackGoal = (goalName: string, params?: GoalParams) => {
      // Envio síncrono, de propósito.
      //
      // Isto rodava dentro de um `requestAnimationFrame` para não competir com
      // a interação (INP), mas o rAF cobrava dois preços altos. Primeiro, ele
      // não roda em aba oculta: um `pricing_plan_clicked` seguido de troca de
      // aba ou fechamento da página ficava na fila e nunca era enviado.
      // Segundo, vários eventos disparados na mesma rajada caíam todos no
      // mesmo frame e chegavam ao DataFast com o mesmo timestamp, em ordem
      // arbitrária — era isso que embaralhava a leitura da jornada.
      //
      // O custo que o rAF tentava evitar não existe: o script da fila no
      // `index.html` define `window.datafast` como um `push` num array, e o
      // script real do DataFast (carregado com `defer`) faz a rede depois.
      // Chamar aqui é empilhar um argumento, não abrir uma requisição.
      try {
        if (typeof window !== "undefined" && window.datafast) {
          if (params) {
            window.datafast(goalName, params);
          } else {
            window.datafast(goalName);
          }
        }
      } catch (error) {
        // Silent fail in production
      }
    };

    /**
     * Identifica o usuário no DataFast para criar perfis persistentes
     * @param userData - Dados do usuário (user_id é obrigatório)
     */
    const identifyUser = (userData: IdentifyParams) => {
      try {
        if (typeof window !== "undefined" && window.datafast) {
          window.datafast("identify", userData);
        }
      } catch (error) {
        // Silent fail in production
      }
    };

    // ========== STANDARD GOALS (CLI COMPATIBILITY) ==========

    /**
     * Goal: Signup completado
     * Visível no painel do DataFast e CLI (ex: --completed-goal signup)
     */
    const trackSignup = (method: "email" | "google" = "email") => {
      trackGoal("signup", { method });
      if (typeof window !== "undefined" && window.gtag) {
        window.gtag("event", "sign_up", { method });
      }
    };

    /**
     * Goal: Lead — a conta foi criada.
     *
     * Dispara num lugar só: em `handleFinalSubmit`, depois de o usuário e a
     * igreja existirem no banco, ao lado de `trackSignup`. Chamar de qualquer
     * outro ponto é o defeito que este comentário existe para impedir.
     *
     * Antes disparava em três pontos, e nenhum deles era um cadastro: ao abrir
     * a `/register`, no efeito que lê `?plan=` da URL, e a cada troca de plano
     * no modal. Uma pessoa que abriu a página de cadastro, trocou de plano
     * duas vezes e desistiu contava como quatro leads. O painel tinha um funil
     * onde `lead` era maior que `register_viewed` e nenhum lead virava venda.
     */
    const trackLead = () => {
      trackGoal("lead");
    };

    /**
     * Goal: Visualização de ajuda/docs
     * Visível no painel do DataFast e CLI (ex: --filter page:contains=/docs)
     */
    const trackHelpViewed = (pagePath: string) => {
      trackGoal("help_viewed", { path: pagePath });
    };

    // ========== LANDING PAGE EXISTING ==========

    /**
     * Goal 1: Usuário acessa a landing page
     */
    const trackLandingView = () => {
      trackGoal("landing_view");
    };

    /**
     * Goal 3: Usuário clica em um plano no pricing
     */
    const trackPricingPlanClicked = (
      planType: string,
      planPrice: number,
      billingCycle: string,
    ) => {
      trackGoal("pricing_plan_clicked", {
        plan_type: planType,
        plan_price: planPrice.toString(),
        billing_cycle: billingCycle,
      });
    };

    // ========== REGISTRATION ==========

    /**
     * Goal 5: Usuário completa passo 1 (dados da conta)
     */
    const trackRegistrationStep1 = () => {
      trackGoal("registration_step_1");
    };

    /**
     * Goal 6: Usuário completa passo 2 (dados da igreja)
     */
    /** `howFound` é o value de "Como conheceu o Ecclesiato?" (lib/how-found.ts). */
    const trackRegistrationStep2 = (howFound?: string) => {
      trackGoal(
        "registration_step_2",
        howFound ? { how_found: howFound } : undefined,
      );
    };

    /**
     * Goal 7: a pessoa trocou de plano dentro do cadastro, pelo modal.
     *
     * Ponto único de disparo. Havia um segundo, no efeito que lê a URL em
     * `multi-step-register.tsx`, condicionado a um `?price=` que nenhum link
     * do site jamais mandou — nem a seção de preços, nem o rodapé, nem os
     * CTAs do blog, que vão todos para `/register?plan=X&period=monthly`. Era
     * um ramo morto que fazia o goal parecer instrumentado nos dois caminhos.
     *
     * Quem escolhe o plano na landing e não troca de ideia não passa por
     * aqui: esse clique é o `pricing_plan_clicked`.
     */
    const trackPlanSelected = (planType: string, planPrice: number) => {
      trackGoal("plan_selected", {
        plan_type: planType,
        plan_price: planPrice.toString(),
      });
    };

    // ========== CHECKOUT ==========

    /**
     * Goal 8: Usuário clica em "Ir para Pagamento"
     */
    const trackCheckoutInitiated = (planType: string, planPrice: number) => {
      trackGoal("checkout_initiated", {
        plan_type: planType,
        plan_price: planPrice.toString(),
      });
      if (typeof window !== "undefined" && window.gtag) {
        window.gtag("event", "begin_checkout", {
          currency: "BRL",
          value: planPrice,
          items: [{ item_name: planType, price: planPrice }],
        });
      }
    };

    // ========== POST-PURCHASE ==========

    const trackPurchaseCompleted = (
      planType: string,
      planPrice: number,
      subscriptionId: string,
    ) => {
      trackGoal("purchase_completed", {
        plan_type: planType,
        plan_price: planPrice.toString(),
        revenue: planPrice, // Visível como propriedade customizada no painel lateral
        subscription_id: subscriptionId,
      });

      // Google Ads Conversion tracking & GA4 Purchase Event
      if (typeof window !== "undefined" && window.gtag) {
        // Ads Conversion
        window.gtag("event", "conversion", {
          send_to: "AW-16555632153/MWD8COeNgq4ZEJnMq9Y9",
          value: planPrice || 1.0,
          currency: "BRL", // Substitua por "USD" se for o caso
          transaction_id: subscriptionId,
        });

        // GA4 Purchase
        window.gtag("event", "purchase", {
          transaction_id: subscriptionId,
          value: planPrice || 1.0,
          currency: "BRL",
          items: [{ item_name: planType, price: planPrice }],
        });
      }
    };

    /**
     * Goal 10: Usuário acessa o dashboard pela primeira vez
     */
    const trackDashboardFirstAccess = (planType: string) => {
      trackGoal("dashboard_first_access", {
        plan_type: planType,
      });
    };

    // ========== ONBOARDING (/onboarding) ==========

    /**
     * A pageview diz só que a pessoa abriu `/onboarding`; o trilho inteiro
     * acontece na mesma URL. Estes goals contam a jornada dentro dele: em que
     * passo entrou, o que viu, o que salvou, o que pulou e onde saiu. O passo
     * vai como parâmetro (`step` é o id de ONBOARDING_STEPS, ou "done" para a
     * tela final) para todos caberem no mesmo relatório.
     *
     * Abandono não tem goal próprio — fechar a aba não avisa ninguém. Ele se lê
     * pelo último `onboarding_step_viewed` de quem não tem `onboarding_finished`.
     */
    const trackOnboardingStarted = (entryStep: string, stepsDone: number) => {
      trackGoal("onboarding_started", {
        entry_step: entryStep,
        steps_done: stepsDone.toString(),
      });
    };

    const trackOnboardingStepViewed = (step: string, position: number) => {
      trackGoal("onboarding_step_viewed", {
        step,
        position: position.toString(),
      });
    };

    /** "Continuar" gravou e avançou. `saved` diz se havia algo a gravar. */
    const trackOnboardingStepContinued = (
      step: string,
      saved: boolean,
      wasDone: boolean,
    ) => {
      trackGoal("onboarding_step_continued", {
        step,
        saved: saved ? "true" : "false",
        was_done: wasDone ? "true" : "false",
      });
    };

    /** "Continuar" não avançou: o passo recusou (`rejected`) ou quebrou (`error`). */
    const trackOnboardingStepFailed = (
      step: string,
      reason: "rejected" | "error",
    ) => {
      trackGoal("onboarding_step_failed", { step, reason });
    };

    const trackOnboardingStepSkipped = (step: string) => {
      trackGoal("onboarding_step_skipped", { step });
    };

    const trackOnboardingStepBack = (
      from: string,
      to: string,
      via: "button" | "stepper" | "resume",
    ) => {
      trackGoal("onboarding_step_back", { from, to, via });
    };

    /** Saiu pelo "Fazer isso depois" do topo, antes da tela final. */
    const trackOnboardingExited = (step: string, stepsDone: number) => {
      trackGoal("onboarding_exited", {
        step,
        steps_done: stepsDone.toString(),
      });
    };

    const trackOnboardingFinished = (allDone: boolean, stepsDone: number) => {
      trackGoal("onboarding_finished", {
        all_done: allDone ? "true" : "false",
        steps_done: stepsDone.toString(),
      });
    };

    // ========== GENERIC ==========

    /**
     * Goal genérico para outros eventos customizados
     */
    const trackCustomGoal = (goalName: string, params?: GoalParams) => {
      trackGoal(goalName, params);
    };

    // ========== LANDING PAGE INTERACTIONS ==========

    const trackHeroCtaClicked = () => trackGoal("hero_cta_clicked");
    const trackHeroLearnMoreClicked = () =>
      trackGoal("hero_learn_more_clicked");
    const trackHeroVideoClicked = () => trackGoal("hero_video_clicked");

    const trackSectionViewed = (sectionName: string) => {
      trackGoal(`${sectionName}_viewed`);
    };

    const trackLoginCompleted = (method: "email" | "google") => {
      trackGoal("login_completed", { method });
      if (typeof window !== "undefined" && window.gtag) {
        window.gtag("event", "login", { method });
      }
    };

    const trackNavClicked = (itemName: string) =>
      trackGoal("nav_clicked", { item: itemName });
    const trackLoginClicked = () => trackGoal("login_clicked");
    const trackStartClicked = () => trackGoal("start_clicked");
    const trackSocialProofClicked = (churchName: string) =>
      trackGoal("social_proof_clicked", { church: churchName });
    const trackWhatsappClicked = (location: string) =>
      trackGoal("whatsapp_clicked", { location });
    const trackFaqExpanded = (questionId: string) =>
      trackGoal("faq_expanded", { question_id: questionId });
    const trackContactSubmit = () => trackGoal("contact_submit");
    const trackFooterLinkClicked = (linkName: string) =>
      trackGoal("footer_link_clicked", { link: linkName });

    // Features interactions
    const trackFeaturesCategoryChanged = (category: string) => {
      trackGoal("features_category_changed", { category });
    };

    const trackFeaturesFeatureClicked = (featureTitle: string) => {
      trackGoal("features_feature_clicked", { feature: featureTitle });
    };

    const trackFeaturesImageExpanded = (imageSrc: string) => {
      const imageName = imageSrc.split("/").pop() || "unknown";
      trackGoal("features_image_expanded", { image: imageName });
    };

    // ========== FEATURE MODULE PAGES (/features/{slug}) ==========

    /**
     * A pageview do DataFast diz apenas quem chegou em `/features/{slug}`.
     * Os goals abaixo dizem o que a pessoa leu antes de sair: o slug vai como
     * parâmetro (e não no nome do goal) para todos os módulos caberem no mesmo
     * relatório, comparáveis entre si.
     */
    const trackFeatureModuleViewed = (slug: string, moduleName: string) => {
      trackGoal("feature_module_viewed", { slug, module: moduleName });
    };

    /** Primeira vez que uma seção da página do módulo entra na tela. */
    const trackFeatureSectionViewed = (slug: string, section: string) => {
      trackGoal("feature_section_viewed", { slug, section });
    };

    /** Marcos de 25/50/75/100% de rolagem — o quanto da página foi percorrido. */
    const trackFeatureScrollDepth = (slug: string, percentage: number) => {
      trackGoal("feature_module_scroll", {
        slug,
        percentage: percentage.toString(),
      });
    };

    const trackFeatureCtaClicked = (slug: string, cta: string) => {
      trackGoal("feature_module_cta_clicked", { slug, cta });
    };

    /** Saída para outro módulo ou para um post do blog a partir da página. */
    const trackFeatureRelatedClicked = (
      slug: string,
      type: "module" | "post",
      target: string,
    ) => {
      trackGoal("feature_related_clicked", { slug, type, target });
    };

    // ========== COMPARATIVOS (/comparar) ==========

    /**
     * Estas páginas existem para receber uma busca comercial de fundo de funil
     * ("ecclesiato ou X"), então o que interessa medir não é audiência: é se a
     * pessoa leu a parte em que declaramos o que o outro sistema faz melhor e
     * ainda assim seguiu. O slug vai como parâmetro para os comparativos
     * ficarem comparáveis entre si no mesmo relatório.
     */
    /**
     * Uma página `/comparar/[slug]` foi aberta.
     *
     * O goal se chamava `comparison_viewed`, o mesmo nome que a seção de
     * comparação da landing gera por `trackSectionViewed("comparison")`. Duas
     * coisas diferentes — rolar até um bloco da home e abrir uma página de
     * concorrente — caíam no mesmo funil do painel, e a única forma de
     * separá-las era notar que uma vinha com `slug` e a outra não. O nome
     * segue o padrão que o próprio arquivo já usa em `comparison_hub_viewed`.
     */
    const trackComparisonViewed = (slug: string, competitor: string) => {
      trackGoal("comparison_page_viewed", { slug, competitor });
    };

    const trackComparisonHubViewed = () => {
      trackGoal("comparison_hub_viewed");
    };

    /** Primeira vez que uma seção do comparativo entra na tela. */
    const trackComparisonSectionViewed = (slug: string, section: string) => {
      trackGoal("comparison_section_viewed", { slug, section });
    };

    const trackComparisonCtaClicked = (slug: string, cta: string) => {
      trackGoal("comparison_cta_clicked", { slug, cta });
    };

    /** Saída do comparativo para um módulo, um post ou outro comparativo. */
    const trackComparisonRelatedClicked = (
      slug: string,
      type: "module" | "post" | "comparison",
      target: string,
    ) => {
      trackGoal("comparison_related_clicked", { slug, type, target });
    };

    // ========== QUIZ DE RECOMENDAÇÃO ==========

    /**
     * O quiz sempre recomenda o Ecclesiato, mas declara a ressalva quando a
     * resposta encosta num limite do produto. É isso que `recommendation`
     * carrega: "ecclesiato" puro, ou "ecclesiato-{ressalva}" — multi-sede,
     * celulas, comunicacao, orcamento. A distribuição dessas ressalvas é a
     * métrica que importa: ela diz quanto do tráfego chega pedindo o que não
     * temos, número que diz mais sobre a origem do tráfego do que qualquer
     * taxa de conversão.
     */
    const trackQuizStarted = (variant: string) => {
      trackGoal("comparison_quiz_started", { variant });
    };

    const trackQuizAnswered = (question: string, answer: string) => {
      trackGoal("comparison_quiz_answered", { question, answer });
    };

    const trackQuizResult = (recommendation: string) => {
      trackGoal("comparison_quiz_result", { recommendation });
    };

    // ========== DIAGNÓSTICO (/diagnostico) ==========

    /**
     * O quiz inteiro acontece numa URL só, então a pageview diz apenas que a
     * pessoa chegou. Estes goals contam o resto: em que pergunta parou, o que
     * respondeu, quanto rolou, o que o resultado disse e para onde ela foi.
     *
     * Nomes próprios, e não os `comparison_quiz_*`: são instrumentos com
     * propósitos diferentes, e o funil de um não pode inflar o do outro.
     *
     * Abandono não tem goal próprio, pela mesma razão do onboarding — fechar a
     * aba não avisa ninguém. Ele se lê pelo último `diagnostico_question_viewed`
     * de quem não tem `diagnostico_result`. É por isso que o goal de pergunta
     * vista dispara uma vez por pergunta em cada rodada: com repetição, o
     * "último visto" deixaria de ser o ponto de saída.
     */
    const trackDiagnosticoViewed = () => {
      trackGoal("diagnostico_viewed");
    };

    /** Primeira vez que cada pergunta aparece numa rodada. */
    const trackDiagnosticoQuestionViewed = (
      question: string,
      position: number,
      total: number,
    ) => {
      trackGoal("diagnostico_question_viewed", {
        question,
        position: position.toString(),
        total: total.toString(),
      });
    };

    /** A primeira resposta da rodada — quem só leu a pergunta não começou. */
    const trackDiagnosticoStarted = (round: number) => {
      trackGoal("diagnostico_started", { round: round.toString() });
    };

    /** `changed` marca a resposta refeita depois de voltar com outro valor. */
    const trackDiagnosticoAnswered = (
      question: string,
      answer: string,
      position: number,
      changed: boolean,
    ) => {
      trackGoal("diagnostico_answered", {
        question,
        answer,
        position: position.toString(),
        changed: changed ? "true" : "false",
      });
    };

    const trackDiagnosticoBack = (from: string, to: string) => {
      trackGoal("diagnostico_back", { from, to });
    };

    /**
     * O resultado. `priority` é a área de nota mais baixa, ou "em-ordem" — a
     * distribuição dele é a métrica principal da página: diz que dor o
     * tráfego orgânico traz. `scores` leva as notas numa string só
     * ("acolhimento:0,escalas:17,…") para caber no limite de dez parâmetros.
     */
    const trackDiagnosticoResult = (params: {
      priority: string;
      size: string;
      model: string;
      who: string;
      hours: number;
      scores: string;
      plan: string;
      durationSeconds: number;
      round: number;
    }) => {
      trackGoal("diagnostico_result", {
        priority: params.priority,
        size: params.size,
        model: params.model,
        who: params.who,
        hours: params.hours.toString(),
        scores: params.scores,
        plan: params.plan,
        duration_s: params.durationSeconds.toString(),
        round: params.round.toString(),
      });
    };

    /**
     * "Começar de novo" (no quiz), "Refazer o diagnóstico" (no resultado) ou
     * "Fazer o da sua igreja" (em um resultado recebido por link — é quem
     * recebeu virando quem responde).
     */
    const trackDiagnosticoRestarted = (
      from: "quiz" | "result" | "shared",
      position: number,
    ) => {
      trackGoal("diagnostico_restarted", {
        from,
        position: position.toString(),
      });
    };

    /**
     * Saída a partir do resultado. As três primeiras são de compra — criar a
     * conta, ver os planos, levar a proposta à reunião. As outras três são as
     * saídas de quem não vai comprar agora: o módulo da área, a ferramenta
     * grátis dela e a comunidade. Separar as duas famílias é o que mostra se a
     * página converte ou só educa.
     */
    const trackDiagnosticoCtaClicked = (
      cta:
        | "register"
        | "pricing"
        | "proposta"
        | "feature"
        | "ferramenta"
        | "comunidade"
        | "whatsapp",
      plan: string,
      priority: string,
    ) => {
      trackGoal("diagnostico_cta_clicked", { cta, plan, priority });
    };

    /**
     * O resultado foi mandado para alguém. Em igreja quem responde raramente
     * decide, então este é o goal que diz se o resultado chegou ao pastor.
     */
    const trackDiagnosticoShared = (
      channel: "whatsapp" | "copy",
      priority: string,
    ) => {
      trackGoal("diagnostico_shared", { channel, priority });
    };

    /**
     * Alguém abriu um resultado que recebeu por link. A razão entre este e o
     * `diagnostico_shared` diz quantos envios foram abertos do outro lado.
     */
    const trackDiagnosticoSharedOpened = (priority: string) => {
      trackGoal("diagnostico_shared_opened", { priority });
    };

    /**
     * O convite do diagnóstico no fim de um post do blog foi clicado. `area`
     * é a área do convite, ou "geral" nos posts de gestão sem área própria.
     */
    const trackBlogDiagnosticoClicked = (slug: string, area: string) => {
      trackGoal("blog_diagnostico_clicked", { slug, area });
    };

    /**
     * Primeira vez que um bloco fica um segundo na tela. No resultado, é o que
     * diz se a pessoa chegou até a oferta ou parou nas notas — no celular o
     * resultado tem várias telas de altura.
     */
    const trackDiagnosticoSectionViewed = (section: string) => {
      trackGoal("diagnostico_section_viewed", { section });
    };

    const trackDiagnosticoScrollDepth = (percentage: number) => {
      trackGoal("diagnostico_scroll", { percentage: percentage.toString() });
    };

    // ========== CARTEIRINHA AVULSA (/carteirinha) ==========
    // O funil: abriu a página → passou por cada etapa → abriu o pagamento →
    // criou o pagamento (Pix gerado ou cartão aprovado) → pago. A diferença
    // entre "pagamento criado" e "pago" é o Pix gerado e não pago.

    /** O cartão do produto no fim de um post (lib/blog-produtos.ts). */
    const trackBlogProdutoClicked = (slug: string, produto: string) => {
      trackGoal("blog_produto_clicked", { slug, produto });
    };

    const trackCarteirinhaViewed = (retomado: boolean) => {
      trackGoal("carteirinha_viewed", { retomado: retomado ? "true" : "false" });
    };

    /** Entrou numa etapa (igreja, modelo, pessoas, revisao). */
    const trackCarteirinhaStep = (step: string) => {
      trackGoal("carteirinha_step", { step });
    };

    const trackCarteirinhaCheckoutOpened = (quantity: number, amountCents: number) => {
      trackGoal("carteirinha_checkout_opened", {
        quantity: quantity.toString(),
        amount_cents: amountCents.toString(),
      });
    };

    const trackCarteirinhaPaymentCreated = (method: string, status: string) => {
      trackGoal("carteirinha_payment_created", { method, status });
    };

    /** Visto como pago na tela de situação; uma vez por visita à tela. */
    const trackCarteirinhaPaid = (quantity: number) => {
      trackGoal("carteirinha_paid", { quantity: quantity.toString() });
    };

    // ========== REGISTRATION FLOW ==========

    const trackRegisterViewed = (params: { plan: string; period: string }) => {
      trackGoal("register_viewed", {
        plan: params.plan,
        period: params.period,
      });
    };

    const trackCheckoutRedirect = () => {
      trackGoal("checkout_redirect");
    };

    // ========== BLOG ==========

    const trackBlogViewed = () => {
      trackGoal("blog_viewed");
    };

    const trackBlogPostViewed = (slug: string, title: string) => {
      trackGoal("blog_post_viewed", { slug, title });
    };

    const trackBlogPostLiked = (slug: string) => {
      trackGoal("blog_post_liked", { slug });
    };

    const trackBlogPostShared = (slug: string) => {
      trackGoal("blog_post_shared", { slug });
    };

    const trackBlogPostCommented = (slug: string) => {
      trackGoal("blog_post_commented", { slug });
    };

    const trackBlogTagFiltered = (tag: string) => {
      trackGoal("blog_tag_filtered", { tag });
    };

    const trackBlogSearch = (query: string) => {
      trackGoal("blog_search", { query });
    };

    const trackBlogPostScroll = (slug: string, percentage: number) => {
      trackGoal("blog_post_scroll", {
        slug,
        percentage: percentage.toString(),
      });
    };

    // ========== BIO LINKS (PUBLIC) ==========

    const trackBioPageView = (
      slug: string,
      churchId?: string,
      displayName?: string,
    ) => {
      trackGoal("bio_page_viewed", {
        slug,
        church_id: churchId || "",
        display_name: displayName || "",
      });
    };

    const trackBioLinkClicked = (params: {
      slug: string;
      linkId: string;
      linkTitle: string;
      linkType: string;
      url?: string;
    }) => {
      trackGoal("bio_link_clicked", {
        slug: params.slug,
        link_id: params.linkId,
        link_title: params.linkTitle,
        link_type: params.linkType,
        url: params.url || "",
      });
    };

    const trackBioSocialClicked = (params: {
      slug: string;
      platform: string;
      url: string;
    }) => {
      trackGoal("bio_social_clicked", {
        slug: params.slug,
        platform: params.platform,
        url: params.url,
      });
    };

    const trackBioShareClicked = (slug: string) => {
      trackGoal("bio_share_clicked", { slug });
    };

    const trackBioFooterClicked = (slug: string) => {
      trackGoal("bio_footer_clicked", { slug });
    };

    // ========== PUBLIC SCHEDULES ==========

    const trackPublicScheduleView = (params: {
      slug: string;
      churchId: string;
      ministryId: string;
      ministryName: string;
      month: number;
      year: number;
      isPublished: boolean;
    }) => {
      trackGoal("public_schedule_viewed", {
        slug: params.slug,
        church_id: params.churchId,
        ministry_id: params.ministryId,
        ministry_name: params.ministryName,
        month: params.month.toString(),
        year: params.year.toString(),
        is_published: params.isPublished ? "true" : "false",
      });
    };

    const trackPublicScheduleMemberFiltered = (params: {
      slug: string;
      memberId: string;
      memberName: string;
    }) => {
      trackGoal("public_schedule_member_filtered", {
        slug: params.slug,
        member_id: params.memberId || "all",
        member_name: params.memberName || "Todos os integrantes",
      });
    };

    const trackPublicAttendanceView = (params: {
      slug: string;
      churchId: string;
      ministryId: string;
      ministryName: string;
    }) => {
      trackGoal("public_attendance_viewed", {
        slug: params.slug,
        church_id: params.churchId,
        ministry_id: params.ministryId,
        ministry_name: params.ministryName,
      });
    };

    const trackPublicGeneralScheduleView = (params: { churchId: string }) => {
      trackGoal("public_general_schedule_viewed", {
        church_id: params.churchId,
      });
    };

    // ========== MEMBER SELF REGISTRATION (PUBLIC) ==========

    const trackMemberRegistrationPageView = (params: {
      churchId: string;
      churchName?: string;
    }) => {
      trackGoal("member_registration_page_viewed", {
        church_id: params.churchId,
        church_name: params.churchName || "",
      });
    };

    const trackMemberRegistrationSearchAttempted = (params: {
      churchId: string;
    }) => {
      trackGoal("member_registration_search_attempted", {
        church_id: params.churchId,
      });
    };

    const trackMemberRegistrationFormStarted = (params: {
      churchId: string;
    }) => {
      trackGoal("member_registration_form_started", {
        church_id: params.churchId,
      });
    };

    const trackMemberRegistrationSubmitted = (params: {
      churchId: string;
      isChild: boolean;
      isEditing: boolean;
    }) => {
      trackGoal("member_registration_submitted", {
        church_id: params.churchId,
        is_child: params.isChild ? "true" : "false",
        is_editing: params.isEditing ? "true" : "false",
      });
    };

    // ========== VISITOR FORM (PUBLIC) ==========

    const trackVisitorFormPageView = (params: {
      churchId: string;
      churchName?: string;
    }) => {
      trackGoal("visitor_form_page_viewed", {
        church_id: params.churchId,
        church_name: params.churchName || "",
      });
    };

    const trackVisitorDuplicateChecked = (params: { churchId: string }) => {
      trackGoal("visitor_duplicate_checked", {
        church_id: params.churchId,
      });
    };

    const trackVisitorFormSubmitted = (params: {
      churchId: string;
      belongsToChurch?: boolean;
    }) => {
      trackGoal("visitor_form_submitted", {
        church_id: params.churchId,
        belongs_to_church: params.belongsToChurch ? "true" : "false",
      });
    };

    // ========== MEMBER CENTRAL (PUBLIC) ==========

    const trackMemberCentralPageView = (params: {
      churchId: string;
      churchName?: string;
    }) => {
      trackGoal("member_central_page_viewed", {
        church_id: params.churchId,
        church_name: params.churchName || "",
      });
    };

    const trackMemberCentralItemClicked = (params: {
      churchId: string;
      itemTitle: string;
      destination?: string;
    }) => {
      trackGoal("member_central_item_clicked", {
        church_id: params.churchId,
        item_title: params.itemTitle,
        destination: params.destination || "",
      });
    };

    // ========== APELO / DECISÃO FORM (PUBLIC) ==========

    const trackApeloFormPageView = (params: {
      churchId: string;
      churchName?: string;
    }) => {
      trackGoal("apelo_form_page_viewed", {
        church_id: params.churchId,
        church_name: params.churchName || "",
      });
    };

    const trackApeloFormSubmitted = (params: {
      churchId: string;
      baptized: boolean;
    }) => {
      trackGoal("apelo_form_submitted", {
        church_id: params.churchId,
        baptized: params.baptized ? "true" : "false",
      });
    };

    // ========== PRAYER REQUESTS FORM (PUBLIC) ==========

    const trackPrayerRequestPageView = (params: {
      churchId: string;
      churchName?: string;
    }) => {
      trackGoal("prayer_request_page_viewed", {
        church_id: params.churchId,
        church_name: params.churchName || "",
      });
    };

    const trackPrayerRequestSubmitted = (params: {
      churchId: string;
      isAnonymous: boolean;
    }) => {
      trackGoal("prayer_request_submitted", {
        church_id: params.churchId,
        is_anonymous: params.isAnonymous ? "true" : "false",
      });
    };

    // ========== MINISTRY JOURNEY FORM (PUBLIC) ==========

    const trackJourneyFormPageView = (params: {
      churchId: string;
      journeyId: string;
      journeyTitle: string;
      ministryId: string;
    }) => {
      trackGoal("journey_form_page_viewed", {
        church_id: params.churchId,
        journey_id: params.journeyId,
        journey_title: params.journeyTitle,
        ministry_id: params.ministryId,
      });
    };

    const trackJourneyMemberVerified = (params: {
      churchId: string;
      journeyId: string;
      memberId: string;
    }) => {
      trackGoal("journey_member_verified", {
        church_id: params.churchId,
        journey_id: params.journeyId,
        member_id: params.memberId,
      });
    };

    const trackJourneyFormSubmitted = (params: {
      churchId: string;
      journeyId: string;
      memberId: string;
    }) => {
      trackGoal("journey_form_submitted", {
        church_id: params.churchId,
        journey_id: params.journeyId,
        member_id: params.memberId,
      });
    };

    // ========== PUBLIC ROLL CALL ==========

    const trackRollCallPageView = (params: {
      churchId: string;
      churchName?: string;
    }) => {
      trackGoal("roll_call_page_viewed", {
        church_id: params.churchId,
        church_name: params.churchName || "",
      });
    };

    const trackRollCallLoaded = (params: {
      churchId: string;
      ministryId: string;
      ministryName?: string;
    }) => {
      trackGoal("roll_call_loaded", {
        church_id: params.churchId,
        ministry_id: params.ministryId,
        ministry_name: params.ministryName || "",
      });
    };

    const trackRollCallPresenceToggled = (params: {
      churchId: string;
      memberId: string;
      present: boolean;
    }) => {
      trackGoal("roll_call_presence_toggled", {
        church_id: params.churchId,
        member_id: params.memberId,
        present: params.present ? "true" : "false",
      });
    };

    // ========== PUBLIC EVENTS ==========

    const trackPublicEventsPageView = (params: {
      churchId: string;
      churchName?: string;
      totalEvents: number;
    }) => {
      trackGoal("public_events_page_viewed", {
        church_id: params.churchId,
        church_name: params.churchName || "",
        total_events: params.totalEvents.toString(),
      });
    };

    const trackPublicEventClicked = (params: {
      churchId: string;
      eventId: string;
      eventName: string;
    }) => {
      trackGoal("public_event_clicked", {
        church_id: params.churchId,
        event_id: params.eventId,
        event_name: params.eventName,
      });
    };

    const trackPublicEventDetailView = (params: {
      churchId: string;
      eventId: string;
      eventName: string;
    }) => {
      trackGoal("public_event_detail_viewed", {
        church_id: params.churchId,
        event_id: params.eventId,
        event_name: params.eventName,
      });
    };

    // ========== EVENT REGISTRATION MANAGEMENT (PUBLIC) ==========

    const trackRegistrationManagementPageView = (params: {
      churchId: string;
      eventId: string;
      registrationId: string;
      paymentStatus?: string;
    }) => {
      trackGoal("registration_management_page_viewed", {
        church_id: params.churchId,
        event_id: params.eventId,
        registration_id: params.registrationId,
        payment_status: params.paymentStatus || "",
      });
    };

    const trackRegistrationTicketDownloaded = (params: {
      churchId: string;
      eventId: string;
      registrationId: string;
    }) => {
      trackGoal("registration_ticket_downloaded", {
        church_id: params.churchId,
        event_id: params.eventId,
        registration_id: params.registrationId,
      });
    };

    const trackRegistrationPaymentInitiated = (params: {
      churchId: string;
      eventId: string;
      registrationId: string;
      amount?: number;
    }) => {
      trackGoal("registration_payment_initiated", {
        church_id: params.churchId,
        event_id: params.eventId,
        registration_id: params.registrationId,
        amount: params.amount !== undefined ? params.amount : 0,
      });
    };

    // ========== PUBLIC SERVICE COUNT (CONTAGEM DE CULTO) ==========

    const trackServiceCountPageView = (params: {
      churchId: string;
      churchName?: string;
    }) => {
      trackGoal("service_count_page_viewed", {
        church_id: params.churchId,
        church_name: params.churchName || "",
      });
    };

    const trackServiceCountAttendeeAdded = (params: {
      churchId: string;
      memberId: string;
    }) => {
      trackGoal("service_count_attendee_added", {
        church_id: params.churchId,
        member_id: params.memberId,
      });
    };

    const trackServiceCountSubmitted = (params: {
      churchId: string;
      total: number;
      isNominal: boolean;
      serviceType?: string;
    }) => {
      trackGoal("service_count_submitted", {
        church_id: params.churchId,
        total: params.total,
        is_nominal: params.isNominal ? "true" : "false",
        service_type: params.serviceType || "",
      });
    };

    // ========== PRESENÇA POR QR ==========
    // Sem id de pessoa: só a igreja e os números.

    const trackAttendanceQrPageView = (params: {
      churchId: string;
      churchName?: string;
    }) => {
      trackGoal("attendance_qr_page_viewed", {
        church_id: params.churchId,
        church_name: params.churchName || "",
      });
    };

    const trackAttendanceQrIdentified = (params: {
      churchId: string;
      kind: "member" | "visitor";
    }) => {
      trackGoal("attendance_qr_identified", {
        church_id: params.churchId,
        kind: params.kind,
      });
    };

    const trackAttendanceQrRegistered = (params: {
      churchId: string;
      people: number;
    }) => {
      trackGoal("attendance_qr_registered", {
        church_id: params.churchId,
        people: params.people,
      });
    };

    // ========== PUBLIC CHURCH AGENDA ==========

    const trackPublicAgendaPageView = (params: {
      churchId: string;
      churchName?: string;
      totalEvents: number;
    }) => {
      trackGoal("public_agenda_page_viewed", {
        church_id: params.churchId,
        church_name: params.churchName || "",
        total_events: params.totalEvents.toString(),
      });
    };

    const trackPublicAgendaViewModeChanged = (params: {
      churchId: string;
      viewMode: string;
    }) => {
      trackGoal("public_agenda_view_mode_changed", {
        church_id: params.churchId,
        view_mode: params.viewMode,
      });
    };

    const trackPublicAgendaFilterChanged = (params: {
      churchId: string;
      year: number;
      month: string;
    }) => {
      trackGoal("public_agenda_filter_changed", {
        church_id: params.churchId,
        year: params.year.toString(),
        month: params.month,
      });
    };

    // ========== PUBLIC KIDS DIGITAL CARD ==========

    const trackKidsPublicPageView = (params: {
      churchId: string;
      churchName?: string;
    }) => {
      trackGoal("kids_public_page_viewed", {
        church_id: params.churchId,
        church_name: params.churchName || "",
      });
    };

    const trackKidsChildFound = (params: {
      churchId: string;
      childId: string;
    }) => {
      trackGoal("kids_child_found", {
        church_id: params.churchId,
        child_id: params.childId,
      });
    };

    const trackKidsCardDownloaded = (params: {
      churchId: string;
      childId: string;
    }) => {
      trackGoal("kids_card_downloaded", {
        church_id: params.churchId,
        child_id: params.childId,
      });
    };

    // ========== PUBLIC AVAILABILITY (DISPONIBILIDADE DE ESCALAS) ==========

    const trackAvailabilityPageView = (params: {
      churchId: string;
      ministryId: string;
      ministryName?: string;
      month: string;
      year: string;
    }) => {
      trackGoal("availability_page_viewed", {
        church_id: params.churchId,
        ministry_id: params.ministryId,
        ministry_name: params.ministryName || "",
        month: params.month,
        year: params.year,
      });
    };

    const trackAvailabilityMemberSelected = (params: {
      churchId: string;
      ministryId: string;
      memberId: string;
    }) => {
      trackGoal("availability_member_selected", {
        church_id: params.churchId,
        ministry_id: params.ministryId,
        member_id: params.memberId,
      });
    };

    const trackAvailabilitySubmitted = (params: {
      churchId: string;
      ministryId: string;
      memberId: string;
      status?: string;
    }) => {
      trackGoal("availability_submitted", {
        church_id: params.churchId,
        ministry_id: params.ministryId,
        member_id: params.memberId,
        status: params.status || "",
      });
    };

    // ========== PUBLIC DAY PREFERENCES (PREFERÊNCIAS DE DIAS DE SERVIR) ==========

    const trackDayPreferencePageView = (params: {
      churchId: string;
      ministryId: string;
      ministryName?: string;
    }) => {
      trackGoal("day_preference_page_viewed", {
        church_id: params.churchId,
        ministry_id: params.ministryId,
        ministry_name: params.ministryName || "",
      });
    };

    const trackDayPreferenceMemberSelected = (params: {
      churchId: string;
      ministryId: string;
      memberId: string;
    }) => {
      trackGoal("day_preference_member_selected", {
        church_id: params.churchId,
        ministry_id: params.ministryId,
        member_id: params.memberId,
      });
    };

    const trackDayPreferenceSubmitted = (params: {
      churchId: string;
      ministryId: string;
      memberId: string;
      totalServicesSelected: number;
    }) => {
      trackGoal("day_preference_submitted", {
        church_id: params.churchId,
        ministry_id: params.ministryId,
        member_id: params.memberId,
        total_services_selected: params.totalServicesSelected.toString(),
      });
    };

    // ========== PUBLIC GROUP ATTENDANCE (FREQUÊNCIA PÚBLICA DE GRUPOS/CÉLULAS) ==========

    const trackGroupAttendancePageView = (params: {
      churchId: string;
      churchName?: string;
      totalGroups: number;
    }) => {
      trackGoal("group_attendance_page_viewed", {
        church_id: params.churchId,
        church_name: params.churchName || "",
        total_groups: params.totalGroups.toString(),
      });
    };

    const trackGroupAttendanceGroupSelected = (params: {
      churchId: string;
      groupId: string;
      totalMembers: number;
    }) => {
      trackGoal("group_attendance_group_selected", {
        church_id: params.churchId,
        group_id: params.groupId,
        total_members: params.totalMembers.toString(),
      });
    };

    const trackGroupAttendanceSubmitted = (params: {
      churchId: string;
      groupId: string;
      presentCount: number;
      totalMembers: number;
    }) => {
      trackGoal("group_attendance_submitted", {
        church_id: params.churchId,
        group_id: params.groupId,
        present_count: params.presentCount.toString(),
        total_members: params.totalMembers.toString(),
      });
    };

    // ========== PUBLIC CRONOGRAMA DE CULTO ==========

    const trackPublicCronogramaPageView = (params: {
      churchId: string;
      churchName?: string;
      serviceName?: string;
      slug: string;
    }) => {
      trackGoal("public_cronograma_page_viewed", {
        church_id: params.churchId,
        church_name: params.churchName || "",
        service_name: params.serviceName || "",
        slug: params.slug,
      });
    };

    // ========== PUBLIC VOTES / POLLS (VOTAÇÕES / ENQUETES PÚBLICAS) ==========

    const trackPublicVotePageView = (params: {
      churchId: string;
      pollId: string;
      pollTitle?: string;
    }) => {
      trackGoal("public_vote_page_viewed", {
        church_id: params.churchId,
        poll_id: params.pollId,
        poll_title: params.pollTitle || "",
      });
    };

    const trackPublicVoteSubmitted = (params: {
      churchId: string;
      pollId: string;
      candidateCount: number;
      securityLevel?: string;
    }) => {
      trackGoal("public_vote_submitted", {
        church_id: params.churchId,
        poll_id: params.pollId,
        candidate_count: params.candidateCount.toString(),
        security_level: params.securityLevel || "",
      });
    };

    // ========== EVENT REGISTRATION CONFIRMATION ==========

    const trackEventConfirmationPageView = (params: {
      churchId?: string;
      eventId?: string;
      status?: string;
      paymentId?: string;
    }) => {
      trackGoal("event_confirmation_page_viewed", {
        church_id: params.churchId || "",
        event_id: params.eventId || "",
        status: params.status || "",
        payment_id: params.paymentId || "",
      });
    };

    const trackEventTicketDownloaded = (params: {
      churchId?: string;
      eventId?: string;
      registrationId: string;
    }) => {
      trackGoal("event_ticket_downloaded", {
        church_id: params.churchId || "",
        event_id: params.eventId || "",
        registration_id: params.registrationId,
      });
    };

    return {
      // Standard Goals
      trackSignup,
      trackLead,
      trackHelpViewed,

      // Landing Existing
      trackLandingView,
      trackPricingPlanClicked,

      // Landing New
      trackHeroCtaClicked,
      trackHeroLearnMoreClicked,
      trackHeroVideoClicked,
      trackSectionViewed,
      trackNavClicked,
      trackLoginClicked,
      trackLoginCompleted,
      trackStartClicked,
      trackSocialProofClicked,
      trackWhatsappClicked,
      trackFaqExpanded,
      trackContactSubmit,
      trackFooterLinkClicked,
      trackFeaturesCategoryChanged,
      trackFeaturesFeatureClicked,
      trackFeaturesImageExpanded,

      // Feature Module Pages
      trackFeatureModuleViewed,
      trackFeatureSectionViewed,
      trackFeatureScrollDepth,
      trackFeatureCtaClicked,
      trackFeatureRelatedClicked,

      // Comparativos (/comparar)
      trackComparisonViewed,
      trackComparisonHubViewed,
      trackComparisonSectionViewed,
      trackComparisonCtaClicked,
      trackComparisonRelatedClicked,

      // Quiz de recomendação
      trackQuizStarted,
      trackQuizAnswered,
      trackQuizResult,

      // Diagnóstico (/diagnostico)
      trackDiagnosticoViewed,
      trackDiagnosticoQuestionViewed,
      trackDiagnosticoStarted,
      trackDiagnosticoAnswered,
      trackDiagnosticoBack,
      trackDiagnosticoResult,
      trackDiagnosticoRestarted,
      trackDiagnosticoCtaClicked,
      trackDiagnosticoShared,
      trackDiagnosticoSharedOpened,
      trackBlogDiagnosticoClicked,
      // Carteirinha avulsa
      trackBlogProdutoClicked,
      trackCarteirinhaViewed,
      trackCarteirinhaStep,
      trackCarteirinhaCheckoutOpened,
      trackCarteirinhaPaymentCreated,
      trackCarteirinhaPaid,
      trackDiagnosticoSectionViewed,
      trackDiagnosticoScrollDepth,

      // Registration Existing
      trackRegistrationStep1,
      trackRegistrationStep2,
      trackPlanSelected,

      // Registration New
      trackRegisterViewed,
      trackCheckoutRedirect,

      // Checkout
      trackCheckoutInitiated,
      // Post-purchase
      trackPurchaseCompleted,
      trackDashboardFirstAccess,
      // Onboarding
      trackOnboardingStarted,
      trackOnboardingStepViewed,
      trackOnboardingStepContinued,
      trackOnboardingStepFailed,
      trackOnboardingStepSkipped,
      trackOnboardingStepBack,
      trackOnboardingExited,
      trackOnboardingFinished,
      // Identification
      identifyUser,
      // Generic
      trackCustomGoal,
      // Blog
      trackBlogViewed,
      trackBlogPostViewed,
      trackBlogPostLiked,
      trackBlogPostShared,
      trackBlogPostCommented,
      trackBlogTagFiltered,
      trackBlogSearch,
      trackBlogPostScroll,
      // Bio Links
      trackBioPageView,
      trackBioLinkClicked,
      trackBioSocialClicked,
      trackBioShareClicked,
      trackBioFooterClicked,
      // Public Schedules
      trackPublicScheduleView,
      trackPublicScheduleMemberFiltered,
      trackPublicAttendanceView,
      trackPublicGeneralScheduleView,
      // Member Self Registration
      trackMemberRegistrationPageView,
      trackMemberRegistrationSearchAttempted,
      trackMemberRegistrationFormStarted,
      trackMemberRegistrationSubmitted,
      // Visitor Form
      trackVisitorFormPageView,
      trackVisitorDuplicateChecked,
      trackVisitorFormSubmitted,
      // Member Central
      trackMemberCentralPageView,
      trackMemberCentralItemClicked,
      // Apelo Form
      trackApeloFormPageView,
      trackApeloFormSubmitted,
      // Prayer Requests Form
      trackPrayerRequestPageView,
      trackPrayerRequestSubmitted,
      // Journey Form
      trackJourneyFormPageView,
      trackJourneyMemberVerified,
      trackJourneyFormSubmitted,
      // Public Roll Call
      trackRollCallPageView,
      trackRollCallLoaded,
      trackRollCallPresenceToggled,
      // Public Events
      trackPublicEventsPageView,
      trackPublicEventClicked,
      trackPublicEventDetailView,
      // Event Registration Management
      trackRegistrationManagementPageView,
      trackRegistrationTicketDownloaded,
      trackRegistrationPaymentInitiated,
      // Public Service Count
      trackServiceCountPageView,
      trackServiceCountAttendeeAdded,
      trackServiceCountSubmitted,
      // Presença por QR
      trackAttendanceQrPageView,
      trackAttendanceQrIdentified,
      trackAttendanceQrRegistered,
      // Public Church Agenda
      trackPublicAgendaPageView,
      trackPublicAgendaViewModeChanged,
      trackPublicAgendaFilterChanged,
      // Public Kids
      trackKidsPublicPageView,
      trackKidsChildFound,
      trackKidsCardDownloaded,
      // Public Availability
      trackAvailabilityPageView,
      trackAvailabilityMemberSelected,
      trackAvailabilitySubmitted,
      // Public Day Preferences
      trackDayPreferencePageView,
      trackDayPreferenceMemberSelected,
      trackDayPreferenceSubmitted,
      // Public Group Attendance
      trackGroupAttendancePageView,
      trackGroupAttendanceGroupSelected,
      trackGroupAttendanceSubmitted,
      // Public Cronograma
      trackPublicCronogramaPageView,
      // Public Votes
      trackPublicVotePageView,
      trackPublicVoteSubmitted,
      // Event Confirmation
      trackEventConfirmationPageView,
      trackEventTicketDownloaded,
    };
  }, []);
};
